export const homeContentByLocale = {
  'zh-TW': {
    metadata: {
      title: 'SEO/AEO/GEO 工具｜源策 (SrcRank): 您的 AI 內容策略顧問',
      description:
        '源策 (SrcRank) 是小黑老師打造的 SEO/AEO/GEO 工具，協助網站經營者檢查內容是否更容易被搜尋系統理解、被生成式 AI 抽取，並提升成為 AI 引用來源的機會。',
      keywords: ['SEO', 'AEO', 'GEO', '內容優化', 'AI 引用', 'AI 分析'],
      ogTitle: 'SEO/AEO/GEO 工具｜源策 (SrcRank): 您的 AI 內容策略顧問',
      ogDescription:
        '檢查內容是否更容易被搜尋系統理解、被生成式 AI 抽取，並提升成為 AI 引用來源的機會。',
      ogLocale: 'zh_TW',
      twitterTitle: 'SEO/AEO/GEO 工具｜源策 (SrcRank): 您的 AI 內容策略顧問',
      twitterDescription:
        '小黑老師打造的 SEO/AEO/GEO 工具，幫你快速看懂內容是否更容易被理解、抽取與引用。'
    },
    hero: {
      eyebrow: 'SEO / AEO / GEO 工具',
      title: '源策 (SrcRank): 您的 AI 內容策略顧問',
      description:
        '檢查頁面是否更容易被搜尋系統理解、被生成式 AI 抽取，並提升成為 AI 引用來源的機會。',
      points: [
        '支援分析網址或直接貼上內容',
        '聚焦基礎訊號、答案可抽取性與內容採用價值',
        '快速找出真正缺失與優先改善方向'
      ],
      note:
        '如果你想更系統化理解 AEO / GEO，可參考小黑老師的 <a href="https://aeogeo.thinkwithblack.com/" target="_blank" rel="sponsored noopener noreferrer">《內容結構學》</a>。'
    },
    tool: {
      heading: '開始分析你的內容',
      description: '直接輸入網址或貼上內容，讓源策先幫你判斷這篇頁面目前是否具備被理解、被抽取與被引用的基礎。'
    },
    features: {
      heading: '源策會幫你看什麼',
      description: '首頁先把功能講清楚，分析結果再告訴你應該先補哪個缺口。',
      cards: [
        {
          title: '基礎訊號完整度',
          description: '檢查 title、canonical、作者、日期與 schema，確認頁面是否具備被系統正常理解的基本訊號。'
        },
        {
          title: '答案可抽取性',
          description: '檢查標題、小節、段落切分、清單與摘要，判斷內容是否容易被搜尋系統與 AI 抽成答案片段。'
        },
        {
          title: '內容採用價值',
          description: '檢查內容是否有可信度、佐證、第一手經驗與被採用的理由，而不是只有表面資訊。'
        },
        {
          title: '整體可用度',
          description: '把上面三個面向收斂成整體 readiness，讓你知道是訊號問題、抽取問題，還是內容說服力問題。'
        }
      ]
    },
    explainer: {
      heading: 'AEO / GEO 為什麼現在要一起看',
      description: '這一段只講必要概念，幫你理解源策的功能，也讓搜尋系統與 AI 快速讀懂首頁主題。',
      items: [
        {
          title: 'SEO',
          description: '先讓頁面有機會被找到、被索引、被正常理解。'
        },
        {
          title: 'AEO',
          description: '讓答案更容易被抽取、整理與呈現在問答型搜尋情境。'
        },
        {
          title: 'GEO',
          description: '讓內容在生成式搜尋中更有機會被採用、引用或作為回答的依據。'
        }
      ]
    },
    trust: {
      heading: '為什麼這套邏輯可信',
      description:
        '源策由台灣 SEO 專家邱煜庭（小黑老師）打造，評分邏輯以 Google 官方公開框架、頁面基礎訊號與 AI 抽取邏輯為基礎，用來協助你做內容 readiness audit，而不是保證排名的黑箱分數。',
      bullets: [
        '不是只看關鍵字，而是看頁面是否更容易被理解、抽取與引用',
        '區分真正缺失、可選強化與目前無法判定，避免把建議全部混成一包',
        '保留功能導向與可執行性，不把首頁寫成一篇冗長教學文'
      ]
    },
    faq: {
      heading: '常見問題',
      items: [
        {
          question: 'AEO 跟 SEO 差在哪裡？',
          answer: 'SEO 先解決頁面被找到與被索引的問題，AEO 更聚焦答案是否容易被系統抽取、整理與呈現。'
        },
        {
          question: 'GEO 是不是就是寫給 AI 看？',
          answer: '不是。GEO 不是把內容寫得像機器文件，而是讓內容更容易成為生成式搜尋採用的可信來源。'
        },
        {
          question: 'Google 的 AI 搜尋需要額外特殊 markup 嗎？',
          answer: '不一定。Google 公開說明沒有專門給 AI features 的特殊要求，核心仍是基本 SEO、可索引性與可顯示 snippet 的內容品質。'
        },
        {
          question: '源策適合拿來做什麼？',
          answer: '適合拿來快速檢查單篇頁面目前是卡在基礎訊號、答案可抽取性，還是內容採用價值，幫助你排優先順序。'
        }
      ]
    }
  },
  en: {
    metadata: {
      title: 'SEO/AEO/GEO Tool | SrcRank: Your AI Content Strategy Advisor',
      description:
        'SrcRank is an SEO/AEO/GEO tool built by Black Teacher to help site owners evaluate whether content is easier for search systems to understand, easier for generative AI to extract, and more likely to become an AI-cited source.',
      keywords: ['SEO', 'AEO', 'GEO', 'content optimization', 'AI citation', 'AI analysis'],
      ogTitle: 'SEO/AEO/GEO Tool | SrcRank: Your AI Content Strategy Advisor',
      ogDescription:
        'Evaluate whether content is easier to understand, extract, and cite in search and generative AI systems.',
      ogLocale: 'en_US',
      twitterTitle: 'SEO/AEO/GEO Tool | SrcRank: Your AI Content Strategy Advisor',
      twitterDescription:
        'An SEO/AEO/GEO tool built by Black Teacher to help you assess understandability, extractability, and citation readiness.'
    },
    hero: {
      eyebrow: 'SEO / AEO / GEO Tool',
      title: 'SrcRank: Your AI Content Strategy Advisor',
      description:
        'Check whether a page is easier for search systems to understand, easier for generative AI to extract, and more likely to become an AI-cited source.',
      points: [
        'Analyze URLs or pasted content',
        'Focus on foundation signals, answer extractability, and citation value',
        'See real gaps and what to fix first'
      ],
      note:
        'If you want a more systematic AEO / GEO foundation, you can also explore Black Teacher’s <a href="https://aeogeo.thinkwithblack.com/" target="_blank" rel="sponsored noopener noreferrer">Content Structure</a> course.'
    },
    tool: {
      heading: 'Start analyzing your content',
      description: 'Enter a URL or paste content to see whether the page is structurally ready to be understood, extracted, and cited.'
    },
    features: {
      heading: 'What SrcRank analyzes',
      description: 'The homepage should explain the tool first. The report then tells you which gap matters most.',
      cards: [
        {
          title: 'Foundation signals',
          description: 'Checks title, canonical, author, date, and schema so the page has the basic signals needed to be understood reliably.'
        },
        {
          title: 'Answer extractability',
          description: 'Checks headings, sections, lists, tables, and summaries to see whether the page is easy to turn into answer snippets.'
        },
        {
          title: 'Citation value',
          description: 'Checks trust, evidence, first-hand specifics, and reasons the page would actually be adopted as a source.'
        },
        {
          title: 'Overall readiness',
          description: 'Combines the dimensions above so you can tell whether the page is limited by signals, extractability, or persuasive value.'
        }
      ]
    },
    explainer: {
      heading: 'Why AEO and GEO now belong in the same conversation',
      description: 'This section stays concise. Its job is to clarify the tool and help AI and search systems understand the homepage topic.',
      items: [
        {
          title: 'SEO',
          description: 'Makes the page discoverable, indexable, and understandable at a basic level.'
        },
        {
          title: 'AEO',
          description: 'Makes answers easier to extract and present in question-driven search experiences.'
        },
        {
          title: 'GEO',
          description: 'Improves the chance that content is adopted and cited in generative search workflows.'
        }
      ]
    },
    trust: {
      heading: 'Why this logic is credible',
      description:
        'SrcRank is built by Taiwan SEO strategist Yu-Ting Chiu (Black Teacher). It uses Google’s public guidance, page-level foundation signals, and AI extraction logic to support a content readiness audit rather than promise rankings.',
      bullets: [
        'It does not stop at keywords and looks at whether a page is easier to understand, extract, and cite',
        'It separates missing foundations, optional improvements, and unknowns instead of mixing them together',
        'It keeps the homepage tool-led instead of turning it into a long educational article'
      ]
    },
    faq: {
      heading: 'FAQ',
      items: [
        {
          question: 'How is AEO different from SEO?',
          answer: 'SEO helps a page get found and indexed. AEO focuses more on whether answers can be extracted, organized, and presented.'
        },
        {
          question: 'Does GEO mean writing only for AI?',
          answer: 'No. GEO is not about writing like a machine. It is about making content more usable as a trustworthy source in generative search.'
        },
        {
          question: 'Do Google AI search features require special markup?',
          answer: 'Not necessarily. Google says there are no special extra requirements for AI features beyond core SEO, indexability, and snippet-eligible content.'
        },
        {
          question: 'What is SrcRank best used for?',
          answer: 'It is best for quickly checking whether a single page is limited by foundation signals, answer extractability, or citation value so you can prioritize fixes.'
        }
      ]
    }
  },
  ja: {
    metadata: {
      title: 'SEO/AEO/GEO ツール｜源策 (Gensaku): AI コンテンツ戦略アドバイザー',
      description:
        '源策 (Gensaku) はブラック先生が設計した SEO/AEO/GEO ツールです。検索システムに理解されやすいか、生成 AI に抽出されやすいか、AI に引用される情報源になりやすいかを確認できます。',
      keywords: ['SEO', 'AEO', 'GEO', 'コンテンツ最適化', 'AI引用', 'AI分析'],
      ogTitle: 'SEO/AEO/GEO ツール｜源策 (Gensaku): AI コンテンツ戦略アドバイザー',
      ogDescription:
        '検索と生成 AI の両方で、理解されやすさ・抽出されやすさ・引用されやすさを確認するツールです。',
      ogLocale: 'ja_JP',
      twitterTitle: 'SEO/AEO/GEO ツール｜源策 (Gensaku): AI コンテンツ戦略アドバイザー',
      twitterDescription:
        'ブラック先生が設計した SEO/AEO/GEO ツール。理解・抽出・引用のされやすさを確認できます。'
    },
    hero: {
      eyebrow: 'SEO / AEO / GEO ツール',
      title: '源策 (Gensaku): AI コンテンツ戦略アドバイザー',
      description:
        'ページが検索システムに理解されやすいか、生成 AI に抽出されやすいか、AI に引用される情報源になりやすいかを確認できます。',
      points: [
        'URL 分析と貼り付け分析の両方に対応',
        '基礎シグナル、抽出性、採用価値を確認',
        '本当に足りない箇所と優先修正点を素早く把握'
      ],
      note:
        'AEO / GEO をより体系的に理解したい場合は、ブラック先生の <a href="https://aeogeo.thinkwithblack.com/" target="_blank" rel="sponsored noopener noreferrer">「內容結構學」</a> も参照できます。'
    },
    tool: {
      heading: 'まずは内容を分析',
      description: 'URL を入力するか内容を貼り付けると、そのページが理解・抽出・引用にどれだけ備わっているかを確認できます。'
    },
    features: {
      heading: '源策が見るポイント',
      description: 'まず機能を理解し、その上でレポートでどこを直すべきかを確認できます。',
      cards: [
        {
          title: '基礎シグナルの充足',
          description: 'title、canonical、著者、日付、schema を確認し、ページが安定して理解されるための土台を見ます。'
        },
        {
          title: '答えの抽出しやすさ',
          description: '見出し、小節、リスト、表、要約を見て、答えとして切り出しやすいかを確認します。'
        },
        {
          title: '採用・引用される価値',
          description: '信頼性、根拠、一次情報、採用される理由があるかを確認します。'
        },
        {
          title: '全体の readiness',
          description: '上の指標をまとめ、土台不足なのか、抽出性なのか、説得力なのかを見極めます。'
        }
      ]
    },
    explainer: {
      heading: 'AEO と GEO を一緒に見る理由',
      description: 'この区画は短く保ちます。目的はツールを説明し、AI と検索システムにホームの主題を伝えることです。',
      items: [
        {
          title: 'SEO',
          description: 'ページが見つかり、インデックスされ、基本的に理解される状態を整えます。'
        },
        {
          title: 'AEO',
          description: '答えが抽出されやすく、Q&A 型の検索体験で使われやすい状態を整えます。'
        },
        {
          title: 'GEO',
          description: '生成型検索で採用・引用される可能性を高める方向です。'
        }
      ]
    },
    trust: {
      heading: 'このロジックを信頼できる理由',
      description:
        '源策は台湾の SEO 専門家である邱煜庭（ブラック先生）が設計しました。Google の公開ガイダンス、ページ基礎シグナル、AI 抽出ロジックをもとに、ランキング保証ではなく内容 readiness audit を支援します。',
      bullets: [
        'キーワードだけでなく、理解・抽出・引用のされやすさを見る',
        '真正缺失、任意強化、まだ判定できない項目を分けて提示する',
        'ホームページを長い解説記事ではなく、ツール中心の構成に保つ'
      ]
    },
    faq: {
      heading: 'FAQ',
      items: [
        {
          question: 'AEO と SEO は何が違いますか？',
          answer: 'SEO は見つかりやすさとインデックスを整え、AEO は答えとして抽出されやすいかにより重点を置きます。'
        },
        {
          question: 'GEO は AI 向けだけの書き方ですか？',
          answer: '違います。GEO は機械っぽく書くことではなく、生成型検索に採用されやすい信頼できる情報源に近づける考え方です。'
        },
        {
          question: 'Google の AI 検索には特別な markup が必要ですか？',
          answer: '必ずしも必要ではありません。Google は特別な追加要件よりも、基本 SEO、インデックス可能性、snippet として扱える内容を重視しています。'
        },
        {
          question: '源策は何に使うのが最適ですか？',
          answer: '単一ページが基礎シグナル、抽出性、採用価値のどこで詰まっているかを素早く見極め、優先順位を付けるのに向いています。'
        }
      ]
    }
  }
}

export function getHomePageContent(locale = 'zh-TW') {
  return homeContentByLocale[locale] || homeContentByLocale['zh-TW']
}

export function getHomeStructuredData(locale = 'zh-TW') {
  const content = getHomePageContent(locale)
  const baseUrl = 'https://ragseo.thinkwithblack.com'
  const localePath = locale === 'en' ? '/en' : locale === 'ja' ? '/jp' : ''
  const pageUrl = `${baseUrl}${localePath}`

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: locale === 'ja' ? '源策 (Gensaku)' : '源策 (SrcRank)',
      url: baseUrl
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: locale === 'ja' ? '源策 (Gensaku)' : 'SrcRank',
      url: pageUrl
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: content.faq.items.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    }
  ]
}
